/**
 * GWP Gift Variant Picker
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles two things for the cart's "gift with purchase" cards
 * (see snippets/cart-free-shipping.liquid + snippets/cart-gift-card.liquid):
 *
 *   1. Variant selection: when a gift product has real variants (color/size),
 *      each card renders one <select> per option plus a
 *      <script type="application/json" data-gift-variants> payload of the
 *      product's variants. Changing a select resolves the matching variant
 *      and updates the card's hidden variant-id input, price and button
 *      disabled state.
 *
 *   2. Multi-gift carousel progress: the "Showing X/Y Gifts" text and a custom
 *      progress-fill bar (sharing one row, per design — not the slideshow
 *      component's own built-in scrollbar, which renders as a separate
 *      full-width block) are both updated from the theme's `slideshow:select`
 *      event.
 *
 *   3. Unlocked banner → picker toggle: clicking "SELECT GIFT" (or the
 *      picker's close button) toggles between the compact "FREE GIFT
 *      UNLOCKED" banner and the full "CHOOSE YOUR FREE GIFT" picker panel
 *      for a given offer — both are already server-rendered, this just
 *      flips which one is visible.
 *
 * All handlers are delegated on `document` so they keep working after the
 * cart section is morphed back in from the server (see
 * component-cart-items.js's existing #handleGiftCardSubmit, which this file
 * does not duplicate — form submission stays exactly as it was).
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // This file is now included from two places (snippets/header-actions.liquid
  // AND snippets/cart-free-shipping.liquid, the latter kept as a fallback for
  // the standalone /cart page) so it's guaranteed to be part of the real
  // initial page load regardless of whether the cart starts empty. Both can
  // legitimately end up on the same page at once — this guard makes a second
  // run a no-op instead of double-registering every listener below, which
  // would otherwise e.g. open and immediately re-close the gift picker on a
  // single click (two toggle listeners firing for one click flips the
  // `hidden` attribute twice, landing back where it started).
  if (document.documentElement.dataset.giftVariantPickerLoaded) return;
  document.documentElement.dataset.giftVariantPickerLoaded = 'true';

  function centsToCurrency(cents) {
    if (window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' })
          .format(cents / 100)
          .replace(/\s?AUD/, '');
      } catch (err) {}
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * @param {HTMLElement} card
   * @param {{price: number}} variant
   */
  function updatePriceDisplay(card, variant) {
    const priceEl = card.querySelector('[data-gift-price]');
    if (!priceEl) return;

    const discount = parseInt(card.dataset.offerDiscount || '', 10);
    const origEl = priceEl.querySelector('.mof__card-price-orig');
    const freeEl = priceEl.querySelector('.mof__card-price-free');
    const discEl = priceEl.querySelector('.mof__card-price-disc');

    if (discount === 100) {
      if (origEl) origEl.textContent = centsToCurrency(variant.price);
      if (freeEl) freeEl.textContent = 'FREE';
    } else if (discount > 0 && !isNaN(discount)) {
      const discPrice = variant.price - Math.floor((variant.price * discount) / 100);
      if (origEl) origEl.textContent = centsToCurrency(variant.price);
      if (discEl) discEl.textContent = centsToCurrency(discPrice);
    } else if (!origEl && !freeEl && !discEl) {
      priceEl.textContent = centsToCurrency(variant.price);
    }
  }

  /** @param {HTMLElement} card */
  function getActiveVariants(card) {
    const script = card.querySelector('script[data-gift-variants]');
    if (!script) return null;

    let variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
    return Array.isArray(variants) ? variants : null;
  }

  /**
   * @param {HTMLElement} card
   * @param {string[]} selectedValues
   */
  function findMatchingVariant(card, selectedValues) {
    const variants = getActiveVariants(card);
    if (!variants) return null;

    return (
      variants.find(function (variant) {
        return selectedValues.every(function (value, index) {
          return variant.options && variant.options[index] === value;
        });
      }) || null
    );
  }

  /**
   * Applies a resolved variant (id/price/availability) to the card's hidden input, price and button.
   * @param {HTMLElement} card
   * @param {{id: (string|number), price: number, available: boolean}|null} match
   */
  function applyVariantResult(card, match) {
    const idInput = card.querySelector('[data-gift-variant-id]');
    const submitBtn = card.querySelector('.mof__gift-card-btn');

    if (match && match.available) {
      if (idInput instanceof HTMLInputElement) idInput.value = String(match.id);
      if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = false;
      updatePriceDisplay(card, match);
    } else if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = true;
    }
  }

  /** @param {HTMLElement} card */
  function updateGiftCard(card) {
    // Colour-swap flow (has_colour_choice in cart-gift-card.liquid): the size
    // select's option values ARE variant ids directly — same as
    // you-may-also-like.js's #selectSize, no text-matching needed.
    const idSelect = card.querySelector('[data-gift-size-by-id]');
    if (idSelect instanceof HTMLSelectElement) {
      const variants = getActiveVariants(card);
      const match = (variants && variants.find(function (v) { return String(v.id) === idSelect.value; })) || null;
      applyVariantResult(card, match);
      return;
    }

    const selects = Array.from(card.querySelectorAll('.mof__gift-variant-select')).filter(
      /** @returns {select is HTMLSelectElement} */
      function (select) { return select instanceof HTMLSelectElement; }
    );

    if (!selects.length) {
      // No size/option select on this card (e.g. a colour swap just landed on
      // a single-variant colour) — resolve directly from the active variants.
      const variants = getActiveVariants(card);
      if (!variants) return;
      const match = variants.find(function (v) { return v.available; }) || variants[0] || null;
      applyVariantResult(card, match);
      return;
    }

    const selectedValues = selects.map(function (select) {
      return select.value;
    });

    applyVariantResult(card, findMatchingVariant(card, selectedValues));
  }

  /**
   * Colour = sibling PRODUCT swap (see cart-gift-card.liquid's has_colour_choice
   * branch): the clicked option carries a product handle in `data-handle`.
   * Swaps the card's active variants dataset to that product's variants,
   * regenerates the size <select> from them (defaulting to the first
   * available size, mirroring you-may-also-like.js's #selectColorSwatch), and
   * updates title/vendor text. No image swap (not needed here).
   * @param {HTMLElement} option
   * @param {HTMLElement} dropdown
   */
  function selectGiftColourProduct(option, dropdown) {
    const card = option.closest('[data-gift-picker]');
    const handle = option.dataset.handle;
    if (!(card instanceof HTMLElement) || !handle) return;

    const dataScript = card.querySelector('script[data-gift-color-variants="' + handle + '"]');
    if (!dataScript) return;

    /** @type {{title?: string, vendor?: string, sizeIndex?: number, variants: Array<{id: (string|number), options: string[], available: boolean, price: number}>}} */
    let payload;
    try {
      payload = JSON.parse(dataScript.textContent || '');
    } catch (err) {
      return;
    }
    if (!payload || !Array.isArray(payload.variants)) return;

    const activeVariantsScript = card.querySelector('script[data-gift-variants]');
    if (activeVariantsScript) {
      activeVariantsScript.textContent = JSON.stringify(payload.variants);
    }

    // Size select: one <option> per variant, keyed by variant id — same as
    // you-may-also-like.js's "Update Size Select" (no dedup, no text-matching;
    // sizeSelect.value = firstAvailableId || variants[0].id, same fallback).
    // Each colour-product carries its OWN sizeIndex (see cart-gift-card.liquid)
    // since size isn't always variant.options[0] — e.g. a product with both
    // Colour and Size options has size at index 1, not 0.
    const sizeSelect = card.querySelector('[data-gift-size-by-id]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const sizeIndex = typeof payload.sizeIndex === 'number' ? payload.sizeIndex : 0;
      let html = '';
      /** @type {(string|number) | null} */
      let firstAvailableId = null;

      payload.variants.forEach(function (variant) {
        if (variant.available && firstAvailableId === null) firstAvailableId = variant.id;

        const optionEl = document.createElement('option');
        optionEl.value = String(variant.id);
        optionEl.textContent = (variant.options && variant.options[sizeIndex]) || '';
        if (!variant.available) optionEl.disabled = true;
        html += optionEl.outerHTML;
      });

      sizeSelect.innerHTML = html;
      const fallbackId = firstAvailableId !== null ? firstAvailableId : payload.variants[0] && payload.variants[0].id;
      if (fallbackId !== null && fallbackId !== undefined) sizeSelect.value = String(fallbackId);
    }

    if (payload.title) {
      const titleEl = card.querySelector('[data-gift-card-title]');
      if (titleEl) titleEl.textContent = payload.title;
    }
    if (payload.vendor) {
      const vendorEl = card.querySelector('[data-gift-card-vendor]');
      if (vendorEl) vendorEl.textContent = payload.vendor;
    }

    const activeDot = dropdown.querySelector('[data-gift-color-active-dot]');
    const activeLabel = dropdown.querySelector('[data-gift-color-active-label]');
    const clickedDot = option.querySelector('.mof__gift-color-dot');
    const clickedLabel = option.querySelector('.mof__gift-color-option-label');
    if (activeDot && clickedDot) activeDot.innerHTML = clickedDot.innerHTML;
    if (activeLabel && clickedLabel) activeLabel.textContent = clickedLabel.textContent;

    dropdown.querySelectorAll('.mof__gift-color-option').forEach(function (li) {
      li.classList.toggle('mof__gift-color-option--active', li === option);
    });

    dropdown.removeAttribute('open');

    updateGiftCard(card);
  }

  /**
   * Colour = a real variant-option value on this single product (the
   * fallback path, no linked colour siblings). The clicked option carries
   * the raw option value in `data-value`; updates the hidden native
   * <select> and fires `change` so the existing matching logic above runs
   * unchanged — same interaction as assets/you-may-also-like.js's colour
   * swatch dropdown.
   * @param {HTMLElement} option
   * @param {HTMLElement} dropdown
   */
  function selectGiftColourValue(option, dropdown) {
    const wrap = option.closest('.mof__gift-variant-select-wrap');
    const hiddenSelect = wrap ? wrap.querySelector('select.mof__gift-variant-select') : null;
    if (!(hiddenSelect instanceof HTMLSelectElement)) return;

    const value = option.dataset.value || '';

    hiddenSelect.value = value;
    hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));

    const activeDot = dropdown.querySelector('[data-gift-color-active-dot]');
    const activeLabel = dropdown.querySelector('[data-gift-color-active-label]');
    const clickedDot = option.querySelector('.mof__gift-color-dot');
    if (activeDot && clickedDot) activeDot.innerHTML = clickedDot.innerHTML;
    if (activeLabel) activeLabel.textContent = value;

    dropdown.querySelectorAll('.mof__gift-color-option').forEach(function (li) {
      li.classList.toggle('mof__gift-color-option--active', li === option);
    });

    dropdown.removeAttribute('open');
  }

  /**
   * The multi-gift carousel (snippets/cart-free-shipping.liquid) wraps each
   * gift card in a <slideshow-slide>. The theme's shared slideshow.js
   * unconditionally calls preventDefault() on 'mousedown' anywhere inside a
   * multi-slide carousel (assets/slideshow.js #handleMouseDown) to detect
   * drag-to-swipe — which most browsers treat as cancelling a native
   * <select>'s dropdown-open behavior. Stopping propagation here, in the
   * capture phase (which always runs before the scroller's bubble-phase
   * listener), keeps that preventDefault() from ever firing for our own
   * selects, without touching the shared slideshow component — swiping
   * anywhere else on the slide is unaffected.
   */
  document.addEventListener('mousedown', function (e) {
    if (e.target instanceof Element && e.target.closest('.mof__gift-variant-select')) {
      e.stopPropagation();
    }
  }, true);

  document.addEventListener('change', function (e) {
    if (!(e.target instanceof HTMLElement)) return;

    const select = e.target.closest('.mof__gift-variant-select');
    if (!select) return;

    const card = select.closest('[data-gift-picker]');
    if (!(card instanceof HTMLElement)) return;

    updateGiftCard(card);
  });

  document.addEventListener('click', function (e) {
    // Element, not HTMLElement: clicking directly on the SVG icon inside the
    // toggle button means e.target is an SVGElement (SVGElement does NOT
    // extend HTMLElement), which made `instanceof HTMLElement` bail out
    // before ever reaching closest() — the toggle only worked when the click
    // landed on the button's own HTML box, not its icon.
    if (!(e.target instanceof Element)) return;

    const toggle = e.target.closest('[data-gift-select-toggle]');
    if (!toggle) return;

    const flow = toggle.closest('[data-gift-flow]');
    if (!flow) return;

    const banner = flow.querySelector('[data-gift-banner]');
    const panel = flow.querySelector('[data-gift-picker-panel]');
    if (!banner || !panel) return;

    const opening = panel.hasAttribute('hidden');
    if (opening) {
      panel.removeAttribute('hidden');
      banner.setAttribute('hidden', '');
    } else {
      panel.setAttribute('hidden', '');
      banner.removeAttribute('hidden');
    }

    // The shared slideshow component (assets/slideshow.js) only enables
    // scrolling on a slideshow once its own IntersectionObserver reports it
    // as intersecting (deliberately, to avoid an iOS Safari compositor-layer
    // crash — see that file's comments), setting an `in-viewport` attribute
    // it relies on for `overflow` to allow scroll. That observer starts
    // watching while the picker panel is still `hidden` (zero size), and on
    // Safari specifically, going from `display:none` to visible with no
    // accompanying scroll/resize doesn't reliably make the observer re-fire
    // — so `in-viewport` never gets set and the carousel appears frozen
    // even though the panel itself is genuinely open. Setting it directly
    // here (we know for a fact it's visible the instant we un-hide it)
    // sidesteps relying on that observer for this one case, without
    // touching the shared component.
    if (opening) {
      panel.querySelectorAll('slideshow-component').forEach(function (el) {
        el.setAttribute('in-viewport', '');
      });
    }
  });

  /**
   * Colour swatch dropdown (see cart-gift-card.liquid): a <details>/<summary>
   * picker. An option with `data-handle` means "colour = sibling product" —
   * handled by selectGiftColourProduct(). An option with `data-value` means
   * "colour = a real variant option on this one product" — handled by
   * selectGiftColourValue(). Clicking outside any open dropdown closes it,
   * same interaction as assets/you-may-also-like.js's colour swatch dropdown.
   */
  document.addEventListener('click', function (e) {
    // Element, not HTMLElement — clicking the caret icon (an SVG) or a
    // colour swatch dot rendered as an <img>/nested <span> would otherwise
    // bail out here before closest() ever runs (same SVGElement-vs-
    // HTMLElement gap as the toggle handler above).
    if (!(e.target instanceof Element)) return;

    const option = e.target.closest('.mof__gift-color-option');
    if (option instanceof HTMLElement) {
      const dropdown = option.closest('[data-gift-color-dropdown]');
      if (!(dropdown instanceof HTMLElement)) return;

      if (option.dataset.handle) {
        selectGiftColourProduct(option, dropdown);
      } else {
        selectGiftColourValue(option, dropdown);
      }
      return;
    }

    const clickedDropdown = e.target.closest('[data-gift-color-dropdown]');
    document.querySelectorAll('[data-gift-color-dropdown][open]').forEach(function (details) {
      if (details !== clickedDropdown) details.removeAttribute('open');
    });
  });

  /**
   * Drives the "Showing X/Y Gifts" counter, the progress fill, and the
   * prev/next arrow disabled state directly from scroll position, instead of
   * the theme's `slideshow:select` event / slideshow.js's own `current`
   * index.
   *
   * Why: slideshow.js's #sync() picks whichever visible slide's CENTER is
   * closest to the scroller's LEFT edge as "current" — correct for a
   * one-slide-at-a-time carousel, but wrong here, where ~2 cards are visible
   * at once. With an odd total (e.g. 3 gifts), scrolling all the way to the
   * end never makes the last card "current" by that definition (the 2nd-to-
   * last card stays leftmost), so `current` maxes out one short of the real
   * end. That made the counter stick at "2/3" and left `next.disabled` false
   * forever, since slideshow.js computes it from that same stuck index.
   *
   * Extracted into its own function (not just a scroll-event callback)
   * because `scroll` only ever fires from an actual drag or arrow-click —
   * it never fires on page load or after a cart:update morph, so a freshly
   * rendered carousel with 2 cards genuinely visible up front still showed
   * the template's hardcoded "1" until the customer first interacted with
   * it. Calling this once right after render (and again after every
   * cart:update) keeps the counter honest from the very first paint.
   * @param {Element} scroller
   */
  function updateGiftCarouselCounter(scroller) {
    if (!(scroller instanceof HTMLElement) || scroller.tagName.toLowerCase() !== 'slideshow-slides') return;

    const slideshowEl = scroller.closest('.mof__gift-slideshow');
    if (!(slideshowEl instanceof HTMLElement)) return;

    const ref = slideshowEl.getAttribute('ref');
    if (!ref) return;

    const counterWrap = document.querySelector('[data-gift-counter-for="' + ref + '"]');
    if (!(counterWrap instanceof HTMLElement)) return;

    const total = parseInt(counterWrap.dataset.giftTotal || '', 10);
    const firstSlide = scroller.firstElementChild;
    if (!total || !(firstSlide instanceof HTMLElement)) return;

    const slideWidth = firstSlide.getBoundingClientRect().width;
    const gapValue = window.getComputedStyle(scroller).columnGap || window.getComputedStyle(scroller).gap || '0';
    const gap = parseFloat(gapValue) || 0;
    const step = slideWidth + gap;
    if (!step) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = maxScroll <= 0 || scroller.scrollLeft >= maxScroll - 1;

    let current;
    if (atEnd) {
      current = total;
    } else {
      const rightEdge = scroller.scrollLeft + scroller.clientWidth;
      current = Math.max(1, Math.min(total, Math.round(rightEdge / step)));
    }

    const currentEl = counterWrap.querySelector('[data-gift-counter-current]');
    if (currentEl) currentEl.textContent = String(current);

    const fillEl = counterWrap.querySelector('[data-gift-progress-fill]');
    if (fillEl instanceof HTMLElement) {
      // Tied to `current`/`total` (same numbers the "Showing X/Y" text
      // uses), not raw scroll-position fraction — at the very start,
      // scrollLeft is 0 regardless of how many cards are already visible,
      // which rendered the fill as 0% ("completely white") even when the
      // text correctly read e.g. "2/3".
      const progress = current / total;
      fillEl.style.width = (progress * 100).toFixed(2) + '%';
    }

    const prevBtn = slideshowEl.querySelector('.slideshow-control--previous');
    const nextBtn = slideshowEl.querySelector('.slideshow-control--next');
    if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = atStart;
    if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = atEnd;
  }

  /** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
  const giftCarouselScrollSettleTimers = new WeakMap();
  // Matches the theme's own Scroller class's SCROLL_END_TIMEOUT (assets/
  // scrolling.js) — mobile Safari's momentum/rubber-band scrolling fires far
  // more `scroll` events per second than Chrome (and elastic overscroll can
  // transiently push scrollLeft outside [0, maxScroll]), so committing
  // atStart/atEnd straight to the arrows' `disabled` state on every raw tick
  // made them visibly flicker there. Settling on a short delay collapses a
  // burst of ticks into one update, same as the shared component already
  // does for its own arrows.
  const GIFT_CAROUSEL_SCROLL_SETTLE_MS = 50;

  document.addEventListener(
    'scroll',
    function (e) {
      if (!(e.target instanceof Element)) return;
      const scroller = e.target;

      const existing = giftCarouselScrollSettleTimers.get(scroller);
      if (existing) clearTimeout(existing);

      giftCarouselScrollSettleTimers.set(
        scroller,
        setTimeout(function () {
          giftCarouselScrollSettleTimers.delete(scroller);
          updateGiftCarouselCounter(scroller);
        }, GIFT_CAROUSEL_SCROLL_SETTLE_MS)
      );
    },
    true
  );

  function refreshAllGiftCarouselCounters() {
    document.querySelectorAll('.mof__gift-slideshow slideshow-slides').forEach(updateGiftCarouselCounter);
  }

  // Reading scrollWidth/clientWidth synchronously (or even a frame or two
  // later) can still land before the slideshow-slides element has its real
  // final layout — e.g. while its ancestor picker panel is transitioning
  // from hidden to visible, or the cart drawer itself is still animating
  // open. A too-early read sees maxScroll <= 0 (looks like everything fits,
  // nothing to scroll), so atEnd is wrongly true and the counter shows the
  // full total ("3/3") even though only 2 cards are actually visible.
  // ResizeObserver sidesteps all of that by firing once with the element's
  // ACTUAL size as soon as layout settles (whenever that is), and again on
  // every later resize (drawer transitions, window resize, etc.) — no
  // timing guesswork needed.
  var giftCarouselResizeObserver =
    window.ResizeObserver &&
    new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        updateGiftCarouselCounter(entry.target);
      });
    });

  function observeGiftCarousels() {
    if (giftCarouselResizeObserver) {
      document.querySelectorAll('.mof__gift-slideshow slideshow-slides').forEach(function (scroller) {
        giftCarouselResizeObserver.observe(scroller);
      });
    } else {
      // No ResizeObserver support: fall back to a couple of delayed reads.
      requestAnimationFrame(function () {
        requestAnimationFrame(refreshAllGiftCarouselCounters);
      });
    }
  }

  observeGiftCarousels();
  document.addEventListener('cart:update', function () {
    // Re-observe in case the morph produced new slideshow-slides elements,
    // and refresh directly too (total/current can change without the
    // scroller's own size changing, which ResizeObserver wouldn't catch).
    setTimeout(function () {
      observeGiftCarousels();
      refreshAllGiftCarouselCounters();
    }, 50);
  });

  /**
   * Product id → {offerIdx, thresholdCents} map embedded once in
   * cart-free-shipping.liquid (covers every configured gift product across
   * all 3 offers, regardless of current eligibility).
   * @returns {Record<string, {offerIdx: string, thresholdCents: number}> | null}
   */
  function getGiftOfferMap() {
    const script = document.querySelector('script[data-gift-offer-map]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  /** @returns {string[]} */
  function getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    return sectionIds;
  }

  /**
   * cart-drawer.js's #loadRecommendations decides whether to refetch/
   * replace "You may also like" by comparing cart.items[0].product_id
   * against the id it cached from the last fetch (stored as this element's
   * `data-product-id`). A quantity +/- on an existing line never changes
   * items[0], so that comparison naturally matches and it skips the
   * refetch — no disruption. Adding/removing a GIFT line, though, isn't
   * something a "customers who bought X also bought Y" widget should react
   * to at all, but it still runs through the same comparison; if it
   * happens to disagree with the cached id, #loadRecommendations replaces
   * the container via a raw innerHTML swap that bypasses morph.js, wiping
   * out the mobile widget's expand/collapse state and forcing a visible
   * collapse-then-reopen. Pre-syncing the cached id to match here makes a
   * gift-triggered cart:update look, to that comparison, exactly like the
   * quantity-change case that already doesn't disturb it.
   * @param {{items?: Array<{product_id: (string|number)}>}} cartData
   */
  function keepRecommendationsUndisturbed(cartData) {
    const anchorProductId = cartData && Array.isArray(cartData.items) && cartData.items[0] && cartData.items[0].product_id;
    if (!anchorProductId) return;
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer instanceof HTMLElement) cartDrawer.dataset.productId = String(anchorProductId);
  }

  /**
   * @param {string} sourceId
   * @param {{sections?: Record<string, string>, items?: Array<{product_id: (string|number)}>}} cartData
   * @param {(string|number)} [productId]
   */
  function dispatchGiftCartUpdate(sourceId, cartData, productId) {
    keepRecommendationsUndisturbed(cartData);
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: sourceId,
          data: { source: sourceId, sections: cartData.sections || {}, productId: productId || null }
        }
      })
    );
  }

  /**
   * Full ownership of gift-form submission (cart-gift-card.liquid's
   * data-gift-form). Normally component-cart-items.js's own
   * #handleGiftCardSubmit handles this with a plain /cart/add.js POST — but
   * that doesn't know about "swap": if a DIFFERENT product from the SAME
   * offer is already in the cart, a plain add would leave BOTH free gifts in
   * the cart at once (the swap "price errors" this fixes). Intercepting in
   * the capture phase — which runs before component-cart-items.js's bubble-
   * phase listener on its ancestor <cart-items-component> — lets us fully
   * replace the flow: remove the existing gift for this offer first (if
   * any), THEN add the newly chosen one, dispatching cart:update once at the
   * end, without modifying the shared component.
   * @param {HTMLFormElement} form
   */
  async function handleGiftFormSubmit(form) {
    const card = form.closest('[data-gift-picker]');
    const offerIdx = card instanceof HTMLElement ? card.dataset.mofGiftIndex : null;
    const idInput = form.querySelector('input[name="id"]');
    const newVariantId = idInput instanceof HTMLInputElement ? idInput.value : null;
    if (!newVariantId) return;

    const btn = form.querySelector('.mof__gift-card-btn');
    if (btn instanceof HTMLButtonElement) {
      if (btn.disabled) return;
      btn.dataset.originalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add('mof__gift-card-btn--selected');
      btn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    }

    try {
      if (offerIdx) {
        const offerMap = getGiftOfferMap();
        if (offerMap) {
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          const existing = (cart.items || []).find(
            /** @param {{product_id: (string|number), variant_id: (string|number)}} item */
            function (item) {
              const info = offerMap[String(item.product_id)];
              if (!info) return false;
              return String(info.offerIdx) === String(offerIdx) && String(item.variant_id) !== String(newVariantId);
            }
          );

          if (existing) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: existing.key, quantity: 0 })
            });
          }
        }
      }

      const sectionIds = getCartSectionIds();
      const formData = new FormData(form);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const addRes = await fetch(form.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const addData = await addRes.json();

      if (addData.status) {
        console.error('[gift-variant-picker] add error:', addData.description || addData.message);
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = false;
          btn.classList.remove('mof__gift-card-btn--selected');
          btn.innerHTML = btn.dataset.originalHtml || 'ADD GIFT';
        }
        return;
      }

      // /cart/add.js only returns the single added line item (no `.items`
      // array) — dispatching THAT as cart:update's `resource` starved
      // cart-drawer.js's #loadRecommendations of the `resource.items[0]
      // .product_id` it needs, so it fell back to reading whatever product
      // row was still first in the (pre-morph) DOM. That mismatch made it
      // refetch/replace the "You may also like" block via a raw innerHTML
      // swap that bypasses morph.js entirely — wiping out the mobile
      // expand/collapse toggle it had built, with no way to rebuild itself
      // until a full page reload. Fetching the full cart here (same as the
      // theme's own native add-to-cart flows already do) gives it the real
      // cart.items it expects, so it takes the same fast, correct path a
      // normal add does instead of falling through to that DOM guess.
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      cart.sections = addData.sections || {};
      dispatchGiftCartUpdate('gift-variant-picker', cart, addData.product_id);
    } catch (err) {
      console.error('[gift-variant-picker] swap/add error:', err);
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = false;
        btn.classList.remove('mof__gift-card-btn--selected');
        btn.innerHTML = btn.dataset.originalHtml || 'ADD GIFT';
      }
    }
  }

  document.addEventListener(
    'submit',
    function (e) {
      if (!(e.target instanceof HTMLFormElement)) return;
      if (!e.target.matches('[data-gift-form]')) return;

      e.preventDefault();
      e.stopPropagation();

      handleGiftFormSubmit(e.target);
    },
    true
  );

  /**
   * Auto-removes a gift line once the cart total drops back below its
   * offer's threshold (customer reduced quantity or removed items) — the
   * offer-gated Liquid rendering hides the banner/picker in that case, but
   * the cart LINE ITEM itself has no such gate and would otherwise stay in
   * the cart forever once added.
   * @param {Array<{key: string}>} items
   */
  async function removeGiftLinesSequentially(items) {
    const item = items.shift();
    if (!item) return;

    try {
      const sectionIds = getCartSectionIds();
      /** @type {{id: string, quantity: number, sections?: string, sections_url?: string}} */
      const payload = { id: item.key, quantity: 0 };
      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      dispatchGiftCartUpdate('gift-auto-remove', data);
    } catch (err) {
      console.error('[gift-variant-picker] auto-remove error:', err);
    }

    if (items.length) {
      // Give the morph from this removal a tick to settle before the next.
      setTimeout(function () {
        removeGiftLinesSequentially(items);
      }, 50);
    }
  }

  /** @param {CustomEvent} e */
  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.sourceId === 'gift-auto-remove') return;

    const cart = e.detail && e.detail.resource;
    if (!cart || !Array.isArray(cart.items)) return;

    const offerMap = getGiftOfferMap();
    if (!offerMap) return;

    const subtotal = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.total_price;
    if (typeof subtotal !== 'number') return;

    const toRemove = cart.items.filter(
      /** @param {{key: string, product_id: (string|number), properties: Record<string, string>}} item */
      function (item) {
        const isGiftLine = item.properties && item.properties['_gift'] === 'true';
        if (!isGiftLine) return false;
        const info = offerMap[String(item.product_id)];
        if (!info) return false;
        return subtotal < info.thresholdCents;
      }
    );

    if (!toRemove.length) return;

    removeGiftLinesSequentially(toRemove.slice());
  });
})();
